import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputBase, Modal, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useThemeStyles from '../../hooks/useThemeStyles';
import { scoreItem } from '../../utils/fuzzyMatch';
import { useCommandPalette, readRecentIds, saveRecentId } from './CommandPaletteContext';

const RESULT_LIMIT = 50;
const GROUP_CAP = 8;

const IS_MAC =
  typeof navigator !== 'undefined' && (navigator.platform || '').toUpperCase().includes('MAC');
export const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl K';

/** Split text into contiguous segments so matched runs can be emphasized. */
const buildSegments = (text, indices) => {
  const highlighted = new Set(indices);
  const segments = [];
  let start = 0;
  for (let i = 1; i <= text.length; i += 1) {
    if (i === text.length || highlighted.has(i) !== highlighted.has(start)) {
      segments.push({ text: text.slice(start, i), isMatch: highlighted.has(start), start });
      start = i;
    }
  }
  return segments;
};

const HighlightedText = ({ text, indices, matchColor }) => {
  if (!indices || indices.length === 0) {
    return <span>{text}</span>;
  }
  return (
    <span>
      {buildSegments(text, indices).map((segment) =>
        segment.isMatch ? (
          <Box
            key={`m-${segment.start}`}
            component="span"
            sx={{ color: matchColor, fontWeight: 700 }}
          >
            {segment.text}
          </Box>
        ) : (
          <span key={`t-${segment.start}`}>{segment.text}</span>
        )
      )}
    </span>
  );
};

HighlightedText.propTypes = {
  text: PropTypes.string.isRequired,
  indices: PropTypes.arrayOf(PropTypes.number).isRequired,
  matchColor: PropTypes.string.isRequired,
};

const KeyHint = ({ keys, label, colors }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      component="kbd"
      sx={{
        fontFamily: 'inherit',
        fontSize: '0.6875rem',
        lineHeight: 1,
        px: 0.6,
        py: 0.4,
        borderRadius: '4px',
        border: `1px solid ${colors.border.subtle}`,
        backgroundColor: colors.background.secondary,
        color: colors.text.secondary,
      }}
    >
      {keys}
    </Box>
    <Typography component="span" sx={{ fontSize: '0.6875rem', color: colors.text.muted }}>
      {label}
    </Typography>
  </Box>
);

KeyHint.propTypes = {
  keys: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  colors: PropTypes.object.isRequired,
};

const CommandPalette = () => {
  const { isOpen, close, collectItems } = useCommandPalette();
  const { colors, components } = useThemeStyles();
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  // Collect items from every registered source each time the palette opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    setActiveIndex(0);
    setEntries([]);

    return collectItems((items, order) => {
      setEntries((previous) => {
        const seen = new Set(previous.map((entry) => entry.item.id));
        const additions = items
          .filter((item) => item && item.id && typeof item.perform === 'function')
          .filter((item) => !seen.has(item.id))
          .map((item, index) => ({ item, order, index }));
        if (additions.length === 0) return previous;
        const merged = [...previous, ...additions];
        merged.sort((a, b) => a.order - b.order || a.index - b.index);
        return merged;
      });
    });
  }, [isOpen, collectItems]);

  // rows drive rendering (headers + items); flatItems drive keyboard focus.
  const { rows, flatItems } = useMemo(() => {
    const trimmed = query.trim();

    if (trimmed) {
      const scored = [];
      entries.forEach(({ item }) => {
        const match = scoreItem(trimmed, item);
        if (match) scored.push({ item, score: match.score, titleIndices: match.titleIndices });
      });
      scored.sort((a, b) => b.score - a.score);
      const top = scored.slice(0, RESULT_LIMIT);
      return {
        rows: top.map(({ item, titleIndices }) => ({ type: 'item', item, titleIndices })),
        flatItems: top.map(({ item }) => item),
      };
    }

    const builtRows = [];
    const flat = [];
    const itemsById = new Map(entries.map((entry) => [entry.item.id, entry.item]));
    const recentItems = readRecentIds()
      .map((id) => itemsById.get(id))
      .filter(Boolean);
    const recentIds = new Set(recentItems.map((item) => item.id));

    if (recentItems.length > 0) {
      builtRows.push({ type: 'header', label: 'Recent' });
      recentItems.forEach((item) => {
        builtRows.push({ type: 'item', item, titleIndices: [] });
        flat.push(item);
      });
    }

    const groups = new Map();
    entries.forEach(({ item }) => {
      if (recentIds.has(item.id)) return;
      const label = item.group || 'Other';
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(item);
    });

    groups.forEach((groupItems, label) => {
      builtRows.push({ type: 'header', label });
      groupItems.slice(0, GROUP_CAP).forEach((item) => {
        builtRows.push({ type: 'item', item, titleIndices: [] });
        flat.push(item);
      });
      if (groupItems.length > GROUP_CAP) {
        builtRows.push({
          type: 'more',
          label,
          count: groupItems.length - GROUP_CAP,
        });
      }
    });

    return { rows: builtRows, flatItems: flat };
  }, [entries, query]);

  // Keep the active row valid and visible as results change.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (activeIndex >= flatItems.length) {
      setActiveIndex(Math.max(0, flatItems.length - 1));
    }
  }, [flatItems.length, activeIndex]);

  useEffect(() => {
    const element = document.getElementById(`command-palette-option-${activeIndex}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, rows]);

  const performItem = (item) => {
    if (!item) return;
    saveRecentId(item.id);
    close();
    item.perform();
  };

  const handleKeyDown = (event) => {
    if (flatItems.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % flatItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + flatItems.length) % flatItems.length);
    } else if (event.key === 'Home' && !query) {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End' && !query) {
      event.preventDefault();
      setActiveIndex(flatItems.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      performItem(flatItems[activeIndex]);
    }
  };

  let flatPosition = -1;

  return (
    <Modal
      open={isOpen}
      onClose={close}
      aria-label="Quick search"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.55)' } },
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        sx={{
          position: 'absolute',
          top: '14vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(640px, calc(100vw - 32px))',
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.background.surface,
          color: colors.text.primary,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: '10px',
          boxShadow: components.paper.shadow || '0 16px 48px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${colors.border.subtle}`,
          }}
        >
          <SearchIcon sx={{ color: colors.text.secondary, fontSize: '1.25rem' }} />
          <InputBase
            autoFocus
            fullWidth
            inputRef={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything — or run a command…"
            inputProps={{
              role: 'combobox',
              'aria-expanded': true,
              'aria-controls': 'command-palette-listbox',
              'aria-activedescendant':
                flatItems.length > 0 ? `command-palette-option-${activeIndex}` : undefined,
              'aria-label': 'Search everything',
            }}
            sx={{
              color: colors.text.primary,
              fontSize: '0.9375rem',
              '& input::placeholder': { color: colors.text.muted, opacity: 1 },
            }}
          />
        </Box>

        <Box
          id="command-palette-listbox"
          role="listbox"
          aria-label="Search results"
          sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}
        >
          {rows.length === 0 && (
            <Typography
              sx={{
                px: 2,
                py: 3,
                fontSize: '0.875rem',
                color: colors.text.secondary,
                textAlign: 'center',
              }}
            >
              {query.trim() ? `No matches for “${query.trim()}”` : 'Nothing to show yet'}
            </Typography>
          )}

          {rows.map((row) => {
            if (row.type === 'header') {
              return (
                <Typography
                  key={`header-${row.label}`}
                  sx={{
                    px: 2,
                    pt: 1.25,
                    pb: 0.5,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: colors.text.muted,
                  }}
                >
                  {row.label}
                </Typography>
              );
            }

            if (row.type === 'more') {
              return (
                <Typography
                  key={`more-${row.label}`}
                  sx={{
                    px: 2,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    color: colors.text.muted,
                  }}
                >
                  …and {row.count} more — type to filter
                </Typography>
              );
            }

            flatPosition += 1;
            const optionIndex = flatPosition;
            const isActive = optionIndex === activeIndex;
            const { item } = row;

            return (
              <Box
                key={item.id}
                id={`command-palette-option-${optionIndex}`}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIndex(optionIndex)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => performItem(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mx: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: isActive ? colors.sidebar.headerHover : 'transparent',
                }}
              >
                {item.icon && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: isActive ? colors.interactive.primary : colors.text.secondary,
                      '& svg': { fontSize: '1.125rem' },
                    }}
                  >
                    {item.icon}
                  </Box>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: '0.875rem', color: colors.text.primary, lineHeight: 1.4 }}
                  >
                    <HighlightedText
                      text={item.title}
                      indices={row.titleIndices || []}
                      matchColor={colors.interactive.primary}
                    />
                  </Typography>
                  {item.subtitle && (
                    <Typography
                      noWrap
                      sx={{ fontSize: '0.75rem', color: colors.text.secondary, lineHeight: 1.4 }}
                    >
                      {item.subtitle}
                    </Typography>
                  )}
                </Box>
                {query.trim() && item.group && (
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: colors.text.muted,
                      flexShrink: 0,
                    }}
                  >
                    {item.group}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 2,
            py: 1,
            borderTop: `1px solid ${colors.border.subtle}`,
            backgroundColor: colors.background.secondary,
          }}
        >
          <KeyHint keys="↑↓" label="navigate" colors={colors} />
          <KeyHint keys="↵" label="open" colors={colors} />
          <KeyHint keys="esc" label="dismiss" colors={colors} />
        </Box>
      </Box>
    </Modal>
  );
};

export default CommandPalette;
